import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

// Кадрирование картинки под заданную пропорцию прямо при загрузке.
//
// Владелец сам выбирает кадр: двигает картинку внутри рамки, которая ровно такой формы,
// как место на экране гостя, приближает её — или отдаляет, чтобы вертикальный баннер
// из Instagram поместился целиком, а пустые поля залились выбранным цветом. Раньше
// обрезало приложение — вслепую, по центру, и у такого баннера пропадали цена и товар.
//
// Наружу уходит готовый JPEG нужного размера. Слишком мелкий кадр не принимаем:
// растянутая картинка на телефоне будет мыльной, и никакое кадрирование это не исправит.
const MAX_ZOOM = 3

function ImageCropModal({ file, aspect = 2, outputWidth = 1200, minWidth = 800, onCancel, onDone }) {
	const { t } = useTranslation()

	const frameRef = useRef(null)
	const dragRef = useRef(null)

	const [image, setImage] = useState(null)
	const [frameWidth, setFrameWidth] = useState(0)
	const [zoom, setZoom] = useState(1)
	const [offset, setOffset] = useState({ x: 0, y: 0 })
	const [fill, setFill] = useState('#ffffff')
	const [saving, setSaving] = useState(false)

	const frameHeight = frameWidth / aspect
	const outputHeight = Math.round(outputWidth / aspect)
	const minHeight = Math.round(minWidth / aspect)

	// zoom = 1 — картинка ровно закрывает кадр («покрыть»); меньше — отдалена, вплоть до
	// «вписать целиком» с полями по краям
	const coverScale = image && frameWidth ? Math.max(frameWidth / image.width, frameHeight / image.height) : 1
	const containScale = image && frameWidth ? Math.min(frameWidth / image.width, frameHeight / image.height) : 1
	const minZoom = coverScale ? containScale / coverScale : 1
	const scale = coverScale * zoom
	const displayWidth = image ? image.width * scale : 0
	const displayHeight = image ? image.height * scale : 0
	const hasMargins = displayWidth < frameWidth - 0.5 || displayHeight < frameHeight - 0.5

	// Во сколько раз исходник растянется в готовом файле. Больше чем outputWidth / minWidth
	// (1200 / 800 = 1,5) — мыльно; это то же правило «в кадре не меньше 800×400»
	const upscale = frameWidth ? scale * outputWidth / frameWidth : 0
	const tooSmall = !!image && upscale > outputWidth / minWidth + 0.001
	const sourceWidth = scale ? Math.round(Math.min(image?.width ?? 0, frameWidth / scale)) : 0
	const sourceHeight = scale ? Math.round(Math.min(image?.height ?? 0, frameHeight / scale)) : 0

	useEffect(() => {
		if (!file) {
			setImage(null)
			return
		}
		const url = URL.createObjectURL(file)
		const img = new Image()
		img.onload = () => setImage(img)
		img.src = url
		return () => URL.revokeObjectURL(url)
	}, [file])

	// Ширина рамки — по окну: модалка адаптивная, и кадр заполняет её целиком
	useEffect(() => {
		if (!file) return
		function measure() {
			if (frameRef.current) setFrameWidth(frameRef.current.clientWidth)
		}
		measure()
		const timer = setTimeout(measure, 50)
		window.addEventListener('resize', measure)
		return () => {
			clearTimeout(timer)
			window.removeEventListener('resize', measure)
		}
	}, [file, image])

	// Новая картинка или другая ширина рамки — «покрыть» и по центру
	useEffect(() => {
		if (!image || !frameWidth) return
		setZoom(1)
		setOffset({
			x: (frameWidth - image.width * coverScale) / 2,
			y: (frameHeight - image.height * coverScale) / 2,
		})
	}, [image, frameWidth]) // eslint-disable-line react-hooks/exhaustive-deps

	// Больше кадра — двигается, но без пустот у края; меньше кадра — двигается внутри него
	function clampAxis(value, frame, size) {
		const low = Math.min(0, frame - size)
		const high = Math.max(0, frame - size)
		return Math.min(high, Math.max(low, value))
	}

	function clampOffset(next, width = displayWidth, height = displayHeight) {
		return {
			x: clampAxis(next.x, frameWidth, width),
			y: clampAxis(next.y, frameHeight, height),
		}
	}

	function changeZoom(nextZoom) {
		if (!image) return
		const value = Math.min(MAX_ZOOM, Math.max(minZoom, nextZoom))
		// Масштаб — к центру кадра, а не к левому верхнему углу
		const ratio = value / zoom
		const cx = frameWidth / 2
		const cy = frameHeight / 2
		const nextScale = coverScale * value
		setZoom(value)
		setOffset(clampOffset(
			{ x: cx - (cx - offset.x) * ratio, y: cy - (cy - offset.y) * ratio },
			image.width * nextScale,
			image.height * nextScale,
		))
	}

	function onPointerDown(e) {
		e.preventDefault()
		e.currentTarget.setPointerCapture(e.pointerId)
		dragRef.current = { x: e.clientX, y: e.clientY, offset }
	}

	function onPointerMove(e) {
		const drag = dragRef.current
		if (!drag) return
		setOffset(clampOffset({
			x: drag.offset.x + e.clientX - drag.x,
			y: drag.offset.y + e.clientY - drag.y,
		}))
	}

	function onPointerUp() {
		dragRef.current = null
	}

	function save() {
		if (!image || tooSmall) return
		setSaving(true)

		// Рисуем ровно то, что в рамке, увеличив до размера файла
		const k = outputWidth / frameWidth
		const canvas = document.createElement('canvas')
		canvas.width = outputWidth
		canvas.height = outputHeight
		const context = canvas.getContext('2d')
		// Поля и прозрачность PNG (в JPEG она стала бы чёрной) — выбранным цветом
		context.fillStyle = fill
		context.fillRect(0, 0, outputWidth, outputHeight)
		context.imageSmoothingQuality = 'high'
		context.drawImage(image, offset.x * k, offset.y * k, displayWidth * k, displayHeight * k)

		canvas.toBlob(blob => {
			setSaving(false)
			if (!blob) return
			onDone(new File([blob], 'banner.jpg', { type: 'image/jpeg' }))
		}, 'image/jpeg', 0.88)
	}

	return (
		<Modal show={!!file} animation={false} centered size="lg" onHide={onCancel}>
			<Modal.Header closeButton>
				<Modal.Title>{t('image_crop_title')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className="text-muted mb-3">{t('image_crop_hint')}</p>

				<div
					ref={frameRef}
					className="position-relative overflow-hidden rounded border"
					style={{
						width: '100%',
						aspectRatio: `${aspect} / 1`,
						background: fill,
						touchAction: 'none',
						cursor: dragRef.current ? 'grabbing' : 'grab',
						userSelect: 'none',
					}}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onWheel={e => changeZoom(zoom - e.deltaY * 0.0015)}>
					{image &&
						<img
							src={image.src}
							alt=""
							draggable={false}
							style={{
								position: 'absolute',
								left: offset.x,
								top: offset.y,
								width: displayWidth,
								height: displayHeight,
								maxWidth: 'none',
								pointerEvents: 'none',
							}}
						/>
					}
				</div>

				<div className="d-flex align-items-center gap-3 mt-3">
					<button type="button" className="btn btn-sm btn-outline-secondary text-nowrap" disabled={!image} onClick={() => changeZoom(minZoom)}>
						{t('image_crop_fit')}
					</button>
					<i className="uil-search-minus" />
					<input
						type="range"
						className="form-range flex-grow-1"
						min={minZoom}
						max={MAX_ZOOM}
						step="0.01"
						value={zoom}
						disabled={!image}
						onChange={e => changeZoom(Number(e.target.value))} />
					<i className="uil-search-plus" />
					<button type="button" className="btn btn-sm btn-outline-secondary text-nowrap" disabled={!image} onClick={() => changeZoom(1)}>
						{t('image_crop_cover')}
					</button>
				</div>

				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
					<small className="text-muted">
						{t('image_crop_result', { width: outputWidth, height: outputHeight })}
						{image &&
							<span className={tooSmall ? 'text-danger ms-2' : 'ms-2'}>
								· {t('image_crop_source', { width: sourceWidth, height: sourceHeight })}
							</span>
						}
					</small>
					{hasMargins &&
						<label className="d-flex align-items-center gap-2 mb-0 small text-muted">
							{t('image_crop_fill')}
							<input type="color" value={fill} onChange={e => setFill(e.target.value)}
								style={{ width: 32, height: 24, padding: 0, border: 'none', background: 'none' }} />
						</label>
					}
				</div>

				{tooSmall &&
					<div className="alert alert-danger mt-3 mb-0">
						{t('image_crop_too_small', { width: minWidth, height: minHeight })}
					</div>
				}

				<div className="d-flex gap-2 w-100 mt-3">
					<button type="button" className="btn btn-outline-warning w-100" onClick={onCancel}>
						{t('cancel')}
					</button>
					<button type="button" className="btn btn-primary w-100" disabled={!image || tooSmall || saving} onClick={save}>
						{t('save')}
					</button>
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default ImageCropModal
